import { blinkConstants } from '@thoughtspot/blink-constants';
import * as Logger from '@thoughtspot/logger';
import { enableInferCurrencyFromBrowserAtViz, getZeroDecimalCurrencies, getNarrowCurrencyCodes, isNarrowCurrencySymbolEnabled, isTrimCurrencySpacingEnabled, } from '@thoughtspot/session-service';
import * as specialDataValueFormat from '@thoughtspot/special-data-value-format';
import _ from 'lodash';
import numeral from 'numeral';
import { currencyFormatter, defaultFormatter, formatCurrency, removeLiterals, getDefaultCurrencyCode, parseFloatNumber, PROTO_TO_CATEGORY, PROTO_TO_NEGATIVE_VALUE_FORMAT, PROTO_TO_UNITS, safeNumberFormat, sanitizeFormat, shortFormatter, UNITS_TO_DIVIDING_FACTOR, UNITS_TO_SUFFIX, validateNumberFormat, } from './number-format';
import { CategoryType, NegativeValueFormat, Unit, } from './types/prism';
const _logger = Logger.create('number-util');
const VALUE_ZERO = 0;
const VALUE_ZERO_STRING = '0';
const CURRENCY_CODE_EXTRACTOR_REGEX = /[\d\s\u00A0\.,]+ *(.*)$/;
const DEFAULT_DECIMAL_PRECISION = 2;
const MAX_DECIMAL_DIGITS = 20;
const DEFAULT_COMPACT_SCIENTIFIC_NUMBER_SUFFIX_MAP = {
    3: 'K',
    6: 'M',
    9: 'G',
    12: 'T',
};
function applyNegativeFormat(formattedValue, negativeFormat) {
    if (typeof negativeFormat === 'number') {
        negativeFormat = PROTO_TO_NEGATIVE_VALUE_FORMAT[negativeFormat];
    }
    switch (negativeFormat) {
        case NegativeValueFormat.PrefixDash:
            return `-${formattedValue}`;
        case NegativeValueFormat.SuffixDash:
            return `${formattedValue}-`;
        case NegativeValueFormat.BracesNodash:
            return `(${formattedValue})`;
        default:
            return `-${formattedValue}`;
    }
}
function getAutoUnitForValue(value) {
    if (value >= UNITS_TO_DIVIDING_FACTOR[Unit.Trillion]) {
        return Unit.Trillion;
    }
    if (value >= UNITS_TO_DIVIDING_FACTOR[Unit.Billion]) {
        return Unit.Billion;
    }
    if (value >= UNITS_TO_DIVIDING_FACTOR[Unit.Million]) {
        return Unit.Million;
    }
    if (value >= UNITS_TO_DIVIDING_FACTOR[Unit.Thousands]) {
        return Unit.Thousands;
    }
    return Unit.None;
}
const getLocaleFromCurrencyConfig = (currencyFormatConfig) => {
    const locale = currencyFormatConfig?.locale;
    if (locale === blinkConstants.USER_LOCALE) {
        return enableInferCurrencyFromBrowserAtViz()
            ? locale
            : getDefaultCurrencyCode();
    }
    return locale || getDefaultCurrencyCode();
};
const getCurrencyCode = (currencyFormatConfig) => {
    const locale = getLocaleFromCurrencyConfig(currencyFormatConfig);
    if (locale === blinkConstants.USER_LOCALE) {
        return getDefaultCurrencyCode();
    }
    return locale;
};
function getCurrencyFormatter(localeFormattingOptions, currencyFormatConfig, shouldTrimSpace = false) {
    const currencyCode = getCurrencyCode(currencyFormatConfig);
    if (isNarrowCurrencySymbolEnabled()) {
        if (getNarrowCurrencyCodes().includes(currencyCode))
            localeFormattingOptions.symbolForm = 'narrow';
    }
    return currencyFormatter(currencyCode, localeFormattingOptions, shouldTrimSpace);
}
export const formatDataValue = (value, options = {}) => {
    if (!('noShorten' in options)) {
        options.noShorten = true;
    }
    const specialVal = specialDataValueFormat.getSpecialFormatData(value);
    if (specialVal == '' && value === null) {
        return specialVal;
    }
    if (specialVal) {
        return specialVal;
    }
    if (options.checkOnlySpecialFormat) {
        return value;
    }
    if (isNaN(value)) {
        return value;
    }
    value = +value;
    const isPercent = !!options.isPercent;
    const isCurrency = !!options.isCurrency;
    const needBusinessFormatting = !options.noShorten && !isPercent;
    if (needBusinessFormatting && !isCurrency) {
        return formatBusinessNumber(value);
    }
    const { formatPattern } = options;
    const { isDouble = false } = options;
    let { nDecimal } = options;
    if ((nDecimal === void 0 || nDecimal < 0) && !formatPattern && isDouble) {
        nDecimal = DEFAULT_DECIMAL_PRECISION;
    }
    if (isCurrency && !isPercent) {
        return formatCurrency(value, {
            compact: needBusinessFormatting ? 'short' : void 0,
            currencyCode: options.currencyCode,
            nDecimal,
            formatPattern,
        });
    }
    return defaultFormatter(value, {
        nDecimal,
        formatPattern,
        isPercent,
        isDouble,
    });
};
export function formatNumber(value, isDouble, formatPattern) {
    return defaultFormatter(value, { formatPattern, isDouble });
}
export function formatNumericArray(inputArr, fmt) {
    return inputArr.map(function (num) {
        return defaultFormatter(num, { formatPattern: fmt });
    });
}
export const isValidNumberFormat = validateNumberFormat;
export function formatCompactScientificNumber(num) {
    const suffixMap = DEFAULT_COMPACT_SCIENTIFIC_NUMBER_SUFFIX_MAP;
    const specialValue = specialDataValueFormat.getSpecialFormatData(num);
    if (specialValue) {
        return specialValue;
    }
    if (isNaN(num)) {
        return num;
    }
    num = +num;
    const absNum = Math.abs(num);
    const powersOfTen = [12, 9, 6, 3];
    for (let i = 0; i < powersOfTen.length; i++) {
        const powerOfTen = powersOfTen[i];
        if (!_.has(suffixMap, powerOfTen)) {
            continue;
        }
        const base = Math.pow(10, powerOfTen);
        if (absNum >= base) {
            const suffix = suffixMap[powerOfTen];
            return toFixedNoTrailingZero(num / base, 1) + suffix;
        }
    }
    return toFixedNoTrailingZero(num, 1);
}
export function formatBusinessNumber(num, defaultDecimal = 2) {
    const specialValue = specialDataValueFormat.getSpecialFormatData(num);
    if (specialValue) {
        return specialValue;
    }
    if (isNaN(num)) {
        return num;
    }
    return shortFormatter(num, defaultDecimal);
}
export function parseBusinessNumber(formattedNumber) {
    if (!/^(\-){0,1}[0-9.,]+(T|B|M|K){0,1}$/i.test(formattedNumber)) {
        return Number.NaN;
    }
    return numeral(formattedNumber.toLowerCase()).value();
}
export function scientificFormat(num, placesAfterDecimal, isPercent) {
    const specialValue = specialDataValueFormat.getSpecialFormatData(num);
    if (specialValue) {
        return specialValue;
    }
    if (isNaN(num)) {
        return num;
    }
    if (isPercent) {
        num *= 100;
    }
    num = +num;
    placesAfterDecimal = placesAfterDecimal || DEFAULT_DECIMAL_PRECISION;
    let formattedValue = num.toExponential(placesAfterDecimal);
    if (isPercent) {
        formattedValue += '%';
    }
    return formattedValue;
}
function toFixedNoTrailingZero(num, nDecimal) {
    const formattedNum = num.toFixed(nDecimal);
    const arr = formattedNum.split('.');
    if (arr[1] === '0') {
        return arr[0];
    }
    return formattedNum;
}
function getFormatterConfigMap(absFloatValue, configDetails, zeroDecimals = false) {
    if (typeof configDetails.unit === 'number') {
        configDetails.unit = PROTO_TO_UNITS[configDetails.unit];
    }
    const isAutoFormatted = configDetails.unit === Unit.Auto;
    let unitDetails = configDetails.unit;
    let decimalDetails = configDetails.decimals;
    if (isAutoFormatted) {
        unitDetails = getAutoUnitForValue(absFloatValue);
        if (unitDetails !== Unit.None) {
            decimalDetails = DEFAULT_DECIMAL_PRECISION;
        }
    }
    if (zeroDecimals &&
        (unitDetails === Unit.None ||
            absFloatValue < UNITS_TO_DIVIDING_FACTOR[Unit.Thousands])) {
        decimalDetails = 0;
    }
    const shouldRemoveTrailingZeros = configDetails.removeTrailingZeroes || isAutoFormatted;
    return {
        unitDetails,
        decimalDetails,
        shouldRemoveTrailingZeros,
    };
}
const prefixCustomCurrencyConditionally = (value, safeNumFormat) => {
    if (!_.isNil(value[1])) {
        if (_.isString(value[1])) {
            return `${value[1].toUpperCase()} ${safeNumFormat}`;
        }
    }
    return safeNumFormat;
};
export const shouldHideDecimals = (currencyCode) => {
    const zeroDecimalCurrencies = getZeroDecimalCurrencies();
    if (_.isEmpty(zeroDecimalCurrencies))
        return false;
    return (zeroDecimalCurrencies.includes(currencyCode) ||
        (currencyCode === blinkConstants.USER_LOCALE &&
            zeroDecimalCurrencies.includes(getDefaultCurrencyCode())));
};
function getValueFormattedByConfig(value, formatConfigProp, worksheetFormatConfig) {
    const shouldTrimSpace = isTrimCurrencySpacingEnabled();
    if (_.isNil(formatConfigProp)) {
        return value.toString();
    }
    const formatConfig = _.cloneDeep(formatConfigProp);
    if (typeof formatConfig.category === 'number') {
        formatConfig.category = PROTO_TO_CATEGORY[formatConfig.category];
    }
    const specialVal = specialDataValueFormat.getSpecialFormatData(value);
    if (specialVal) {
        return specialVal;
    }
    let floatValue = parseFloat(value.toString());
    const absFloatValue = Math.abs(floatValue);
    if (formatConfig.category === CategoryType.Number) {
        const configDetails = formatConfig.numberFormatConfig;
        const formatterConfigMap = getFormatterConfigMap(absFloatValue, configDetails);
        const compactValue = absFloatValue /
            UNITS_TO_DIVIDING_FACTOR[formatterConfigMap.unitDetails];
        const suffix = UNITS_TO_SUFFIX[formatterConfigMap.unitDetails];
        const formattedValue = safeNumberFormat({
            style: 'decimal',
            maximumFractionDigits: formatterConfigMap.decimalDetails,
            minimumFractionDigits: formatterConfigMap.shouldRemoveTrailingZeros
                ? 0
                : formatterConfigMap.decimalDetails,
            useGrouping: configDetails.toSeparateThousands,
        }, compactValue);
        const absFormattedValue = !_.isNil(absFloatValue)
            ? `${formattedValue}${suffix}`
            : formattedValue;
        if (absFloatValue && absFloatValue !== floatValue) {
            return applyNegativeFormat(absFormattedValue, configDetails.negativeValueFormat);
        }
        return absFormattedValue;
    }
    if (formatConfig.category === CategoryType.Percentage) {
        const configDetails = formatConfig.percentageFormatConfig;
        return safeNumberFormat({
            style: 'percent',
            maximumFractionDigits: configDetails.decimals,
            minimumFractionDigits: configDetails.removeTrailingZeroes
                ? 0
                : configDetails.decimals,
        }, floatValue);
    }
    if (formatConfig.category === CategoryType.Currency) {
        const configDetails = formatConfig.currencyFormatConfig;
        const shouldRoundDecimals = shouldHideDecimals(configDetails.locale);
        const formatterConfigMap = getFormatterConfigMap(absFloatValue, configDetails, shouldRoundDecimals);
        if (shouldRoundDecimals) {
            floatValue = Math.round(floatValue);
        }
        const compactValue = floatValue /
            UNITS_TO_DIVIDING_FACTOR[formatterConfigMap.unitDetails];
        try {
            const localeFormattingOptions = {
                style: 'symbol',
                maximumFractionDigits: formatterConfigMap.decimalDetails,
                minimumFractionDigits: formatterConfigMap.shouldRemoveTrailingZeros
                    ? 0
                    : formatterConfigMap.decimalDetails,
                useGrouping: configDetails.toSeparateThousands,
            };
            const formatter = getCurrencyFormatter(localeFormattingOptions, configDetails, shouldTrimSpace);
            let formattedValue = formatter(compactValue);
            if (shouldTrimSpace) {
                formattedValue = removeLiterals(formattedValue);
            }
            const currencyCode = formattedValue
                .replace(CURRENCY_CODE_EXTRACTOR_REGEX, '$1')
                .trim();
            const suffix = !_.isNil(floatValue)
                ? UNITS_TO_SUFFIX[formatterConfigMap.unitDetails]
                : '';
            if (/[0-9]$/.test(formattedValue.charAt(0))) {
                return `${formattedValue.replace(currencyCode, '')}${suffix}${currencyCode}`;
            }
            return `${formattedValue}${suffix}`;
        }
        catch (e) {
            _logger.error('Corrupted format config passed, formatting using default config', formatConfig, e);
        }
    }
    if (formatConfig.category === CategoryType.Custom) {
        if (_.isArray(value)) {
            if (value.length === 2) {
                const safeNumFormat = safeNumberFormat({
                    style: 'decimal',
                }, floatValue);
                return prefixCustomCurrencyConditionally(value, safeNumFormat);
            }
        }
        if (!_.isEmpty(worksheetFormatConfig.formatPattern)) {
            const formatPattern = worksheetFormatConfig.formatPattern;
            const sanitizedPattern = sanitizeFormat(worksheetFormatConfig.formatPattern);
            if (!_.isNil(worksheetFormatConfig.columnLocale)) {
                if (validateNumberFormat(sanitizedPattern)) {
                    const currencyCode = worksheetFormatConfig.columnLocale;
                    const formattedValueWithLocaleAndPattern = formatCurrency(floatValue, {
                        currencyCode,
                        formatPattern,
                    });
                    return `${formattedValueWithLocaleAndPattern}`;
                }
                _logger.debug('Corrupted format config passed, formatting using default config', formatConfig);
                return safeNumberFormat({
                    style: 'decimal',
                }, floatValue);
            }
            if (validateNumberFormat(sanitizedPattern)) {
                return safeNumberFormat({
                    style: 'decimal',
                    raw: sanitizedPattern,
                }, floatValue);
            }
        }
    }
    _logger.debug('Corrupted format config passed, formatting using default config', formatConfig);
    return safeNumberFormat({
        style: 'decimal',
    }, floatValue);
}
function parseStringToFloat(num) {
    let numOutput;
    if (_.isNil(num) || _.isEmpty(num.toString())) {
        return null;
    }
    numOutput = parseFloatNumber(num.toString());
    if (_.isNaN(numOutput)) {
        return null;
    }
    return numOutput;
}
function getCurrencySymbol(currencyFormatConfig) {
    const currencyFormatOptions = {
        style: 'symbol',
        maximumFractionDigits: currencyFormatConfig.decimals,
        minimumFractionDigits: currencyFormatConfig.removeTrailingZeroes
            ? 0
            : currencyFormatConfig.decimals,
        useGrouping: currencyFormatConfig.toSeparateThousands,
    };
    const formatter = getCurrencyFormatter(currencyFormatOptions, currencyFormatConfig);
    const formattedValue = formatter(123456);
    const currencyCode = formattedValue
        .replace(CURRENCY_CODE_EXTRACTOR_REGEX, '$1')
        .trim();
    return currencyCode;
}
export { parseStringToFloat, getCurrencySymbol, getLocaleFromCurrencyConfig, getValueFormattedByConfig, DEFAULT_DECIMAL_PRECISION, MAX_DECIMAL_DIGITS, };
//# sourceMappingURL=number-util.js.map